import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { eventAPI, authAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Calendar, MapPin, Users, Video, ArrowLeft, Clock, Trophy, 
  ExternalLink, Loader2, CheckCircle
} from 'lucide-react';

export default function EventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    fetchEvent();
    fetchCurrentUser();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await eventAPI.getOne(eventId);
      setEvent(response.data);
    } catch (error) {
      toast.error('Event not found');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getMe();
      setCurrentUser(response.data);
    } catch (error) {
      // Not logged in
    }
  };

  const handleRegister = async () => {
    if (!currentUser) {
      toast.error('Please sign in to register');
      return;
    }

    setIsRegistering(true);
    try {
      const response = await eventAPI.register(eventId);
      setEvent(prev => ({
        ...prev,
        is_registered: response.data.registered,
        registration_count: prev.registration_count + (response.data.registered ? 1 : -1)
      }));
      toast.success(response.data.registered ? 'Registered successfully!' : 'Registration cancelled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setIsRegistering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-detail-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/events" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Events
        </Link>

        {/* Event Header */}
        {event.image_url && (
          <div className="rounded-xl overflow-hidden mb-6">
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full aspect-video object-cover"
            />
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 mb-6">
          {/* Badges */}
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-purple-100 text-purple-700">{event.type}</Badge>
            <Badge className={event.status === 'upcoming' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
              {event.status}
            </Badge>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" data-testid="event-title">
            {event.title}
          </h1>

          {/* Event Info */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {event.start_date && (
              <div className="flex items-center gap-3 text-slate-600">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="font-medium text-slate-900">Date</p>
                  <p>{format(new Date(event.start_date), 'EEEE, MMMM d, yyyy')}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 text-slate-600">
              {event.is_virtual ? (
                <>
                  <Video className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="font-medium text-slate-900">Location</p>
                    <p>Virtual Event</p>
                  </div>
                </>
              ) : (
                <>
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="font-medium text-slate-900">Location</p>
                    <p>{event.location || 'TBA'}</p>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 text-slate-600">
              <Users className="w-5 h-5 text-indigo-600" />
              <div>
                <p className="font-medium text-slate-900">Participants</p>
                <p>
                  {event.registration_count} registered
                  {event.max_participants && ` / ${event.max_participants} max`}
                </p>
              </div>
            </div>
          </div>

          {/* Register Button */}
          {event.status === 'upcoming' && (
            <Button
              onClick={handleRegister}
              disabled={isRegistering}
              className={event.is_registered ? 'bg-emerald-600' : 'bg-indigo-600'}
              data-testid="register-btn"
            >
              {isRegistering ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : event.is_registered ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : null}
              {event.is_registered ? 'Registered' : 'Register Now'}
            </Button>
          )}
        </div>

        {/* Description */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">About This Event</h2>
            <div className="prose-content text-slate-700">
              <p className="whitespace-pre-wrap">{event.description}</p>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        {event.schedule?.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Schedule
              </h2>
              <div className="space-y-4">
                {event.schedule.map((item, index) => (
                  <div key={index} className="flex gap-4 border-l-2 border-indigo-200 pl-4">
                    <div className="text-sm text-indigo-600 font-medium whitespace-nowrap">
                      {item.time}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.title}</p>
                      {item.description && (
                        <p className="text-sm text-slate-600">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Prizes */}
        {event.prizes?.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Prizes
              </h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {event.prizes.map((prize, index) => (
                  <div key={index} className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">{prize.place}</p>
                    <p className="font-medium text-slate-900">{prize.title}</p>
                    {prize.value && (
                      <p className="text-sm text-slate-600">{prize.value}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sponsors */}
        {event.sponsors?.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Sponsors</h2>
              <div className="flex flex-wrap gap-4">
                {event.sponsors.map((sponsor, index) => (
                  <div key={index} className="text-center">
                    {sponsor.logo && (
                      <img src={sponsor.logo} alt={sponsor.name} className="h-12 object-contain" />
                    )}
                    <p className="text-sm text-slate-600 mt-1">{sponsor.name}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
